import {
  GridColDef,
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { FilterKey } from "hooks/useOmnisearchDatagrid";
import { ParsedOmnisearchResult, useParsedOmnisearch } from "hooks/useParsedOmnisearch";
import React, { createContext, useContext, ReactNode, useMemo } from "react";

/**
 * Enhanced Omnisearch Context
 *
 * This context provides all omnisearch state and actions to child components,
 * eliminating the need for prop drilling or duplicate hook calls.
 *
 * The context is populated by OmnisearchProvider (recommended) or
 * OmnisearchDataDisplay (backwards compatible) and consumed by:
 * - Filter bars (read current filters, update filters)
 * - Charts (read filters for queries, update on click)
 * - Grids (read omnisearch + grid state)
 * - Any component that needs to interact with omnisearch
 *
 * Key design decisions:
 * - Single parse: omnisearch is parsed ONCE and shared via context
 * - Stable actions: setters are stable (useCallback) and won't cause re-renders
 * - Debounced by default: setOmnisearch is debounced for typing
 * - Immediate option: setOmnisearchImmediate for programmatic updates (clicks, clears)
 */

/**
 * Grid-specific state provided by OmnisearchProvider.
 * Only present when using the full OmnisearchProvider pattern.
 */
export interface OmnisearchGridState {
  paginationModel: GridPaginationModel;
  setPaginationModel: (model: GridPaginationModel) => void;
  sortModel: GridSortModel;
  setSortModel: (model: GridSortModel) => void;
  resetSortModel: () => void;
  columns: GridColDef[];
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: (model: GridColumnVisibilityModel) => void;
  setColumnWidthModel: (params: GridColumnResizeParams) => void;
  unsetColumnWidthModel: (field: string) => void;
  resetColumnSettings: () => void;
  columnFilterKeys: FilterKey[];
}

export interface OmnisearchDataContextType {
  /** Current omnisearch string (debounced - only updates after typing stops) */
  omnisearch: string;

  /** Parsed omnisearch with helper methods */
  parsedOmnisearch: ParsedOmnisearchResult;

  /** Update omnisearch (debounced - use for typing) */
  setOmnisearch: (value: string) => void;

  /** Update omnisearch immediately (no debounce - use for clicks, clears, filter applies) */
  setOmnisearchImmediate: (value: string) => void;

  /**
   * Edit multiple key-value pairs in omnisearch.
   * Uses immediate update (no debounce) since this is typically from UI interactions.
   * @example editOmnisearchKeyValues([["status", "active"], ["state", "CA"]])
   */
  editOmnisearchKeyValues: (keyValues: [string, string][]) => void;

  /**
   * Remove a key from omnisearch.
   * Uses immediate update (no debounce).
   * @example removeOmnisearchKeyValue("status")
   */
  removeOmnisearchKeyValue: (key: string) => void;

  /**
   * Get the value for a specific key from omnisearch.
   * @example const status = getOmnisearchValue("status")
   */
  getOmnisearchValue: (key: string) => string | undefined;

  /**
   * Create a URL with the given key-values applied to the current omnisearch.
   * Useful for generating links that preserve/modify filters.
   */
  createUrlFromOmnisearchKeyValues: (params: {
    keyValues: [string, string][];
    keysToKeep?: string[];
  }) => string;

  /**
   * Grid state - only present when using OmnisearchProvider.
   * Use useOmnisearchGrid() hook if you need grid state.
   */
  gridState?: OmnisearchGridState;
}

const OmnisearchDataContext = createContext<OmnisearchDataContextType | undefined>(undefined);

/**
 * Hook to access omnisearch state and actions from context.
 *
 * Must be used within a component tree that has OmnisearchDataDisplay
 * (which provides the context).
 *
 * @throws Error if used outside of OmnisearchDataDisplay
 *
 * @example
 * function StatusFilter() {
 *   const { parsedOmnisearch, editOmnisearchKeyValues } = useOmnisearch();
 *
 *   const currentStatus = parsedOmnisearch.getValue("status");
 *
 *   const handleStatusChange = (status: string) => {
 *     editOmnisearchKeyValues([["status", status]]);
 *   };
 *
 *   return <Select value={currentStatus} onChange={handleStatusChange} />;
 * }
 */
export function useOmnisearch(): OmnisearchDataContextType {
  const context = useContext(OmnisearchDataContext);
  if (!context) {
    throw new Error(
      "useOmnisearch must be used within a component wrapped by OmnisearchDataDisplay"
    );
  }
  return context;
}

/**
 * Hook to optionally access omnisearch context.
 * Returns undefined if not within OmnisearchDataDisplay.
 * Useful for components that can work with or without the context.
 */
export function useOmnisearchOptional(): OmnisearchDataContextType | undefined {
  return useContext(OmnisearchDataContext);
}

/**
 * Hook for grid components that need both omnisearch and grid state.
 *
 * Must be used within OmnisearchProvider (not just OmnisearchDataProvider).
 *
 * @throws Error if used outside of OmnisearchProvider or if gridState is not available
 *
 * @example
 * function MyGrid() {
 *   const { omnisearch, gridState } = useOmnisearchGrid();
 *   const { paginationModel, sortModel, columns } = gridState;
 *   // ... render grid
 * }
 */
export function useOmnisearchGrid(): OmnisearchDataContextType & {
  gridState: OmnisearchGridState;
} {
  const context = useOmnisearch();
  if (!context.gridState) {
    throw new Error(
      "useOmnisearchGrid must be used within OmnisearchProvider. " +
        "If you're using OmnisearchDataDisplay directly, it doesn't provide grid state. " +
        "Wrap your component tree with OmnisearchProvider instead."
    );
  }
  return context as OmnisearchDataContextType & {
    gridState: OmnisearchGridState;
  };
}

/**
 * Hook to optionally access grid state from context.
 * Returns undefined if gridState is not available.
 * Useful for components that can work with or without grid context.
 */
export function useOmnisearchGridOptional():
  | (OmnisearchDataContextType & { gridState: OmnisearchGridState })
  | undefined {
  const context = useOmnisearchOptional();
  if (!context?.gridState) {
    return undefined;
  }
  return context as OmnisearchDataContextType & {
    gridState: OmnisearchGridState;
  };
}

/**
 * Resets the Omnisearch context to `undefined` for its subtree, so a nested
 * `OmnisearchDataGrid` falls back to its own internal (persistenceKey-scoped)
 * state instead of inheriting an ambient `OmnisearchProvider`'s grid state.
 *
 * Use this around self-contained grids rendered inside a page that has its own
 * `OmnisearchProvider` — e.g. a facility picker in a dialog opened from a list
 * page. Without it, the nested grid would inherit the page's sort/filters (e.g.
 * a `staffAssignee` sort that's invalid for the `facilities` query). Portaled
 * dialogs still inherit React context, so this boundary is required even for
 * modals.
 */
export function OmnisearchContextResetBoundary({ children }: { children: ReactNode }) {
  return (
    <OmnisearchDataContext.Provider value={undefined}>{children}</OmnisearchDataContext.Provider>
  );
}

interface OmnisearchDataProviderProps {
  children: ReactNode;
  omnisearch: string;
  setOmnisearch: (value: string) => void;
  setOmnisearchImmediate: (value: string) => void;
  editOmnisearchKeyValues: (keyValues: [string, string][]) => void;
  removeOmnisearchKeyValue: (key: string) => void;
  getOmnisearchValue: (key: string) => string | undefined;
  createUrlFromOmnisearchKeyValues: (params: {
    keyValues: [string, string][];
    keysToKeep?: string[];
  }) => string;
  keyAliases?: Record<string, string>;
  /** Grid state - only provided when using OmnisearchProvider */
  gridState?: OmnisearchGridState;
}

/**
 * Internal provider component.
 *
 * For most use cases, prefer using OmnisearchProvider which provides both
 * omnisearch and grid state. OmnisearchDataDisplay also uses this internally
 * for backwards compatibility.
 */
export function OmnisearchDataProvider({
  children,
  omnisearch,
  setOmnisearch,
  setOmnisearchImmediate,
  editOmnisearchKeyValues,
  removeOmnisearchKeyValue,
  getOmnisearchValue,
  createUrlFromOmnisearchKeyValues,
  keyAliases,
  gridState,
}: OmnisearchDataProviderProps) {
  // Parse omnisearch once, memoized
  const parsedOmnisearch = useParsedOmnisearch(omnisearch, keyAliases);

  // Memoize context value to prevent unnecessary re-renders
  // Note: omnisearch and parsedOmnisearch change together (when filters change)
  // The action functions are already stable (useCallback in useOmnisearchInput)
  const value = useMemo<OmnisearchDataContextType>(
    () => ({
      omnisearch,
      parsedOmnisearch,
      setOmnisearch,
      setOmnisearchImmediate,
      editOmnisearchKeyValues,
      removeOmnisearchKeyValue,
      getOmnisearchValue,
      createUrlFromOmnisearchKeyValues,
      gridState,
    }),
    [
      omnisearch,
      parsedOmnisearch,
      setOmnisearch,
      setOmnisearchImmediate,
      editOmnisearchKeyValues,
      removeOmnisearchKeyValue,
      getOmnisearchValue,
      createUrlFromOmnisearchKeyValues,
      gridState,
    ]
  );

  return <OmnisearchDataContext.Provider value={value}>{children}</OmnisearchDataContext.Provider>;
}
